import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "react-query";
import {
  Box,
  Typography,
  Tab,
  Tabs,
  Paper,
  Stack,
  alpha,
  useTheme,
  Fade,
  Avatar,
} from "@mui/material";
import {
  History,
  Assignment,
  PersonPin,
  AutoGraph,
  PhoneInTalk,
} from "@mui/icons-material";

import { fetchClient } from "../../../../../api/services/Client";
import { QK } from "../../../../../base/qk";
import VisitCall from "./VisitCall";
import DirectVisit from "./DirectVisit";
import Loader from "../../../../../components/Loader/Loader";
import CarePlanConfiguration from "./CarePlanConfiguration";

const VisitAnalysis = () => {
  const { id: clientID } = useParams();
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);

  const { data: client, isLoading } = useQuery(
    [QK.clientDetails, clientID],
    () => fetchClient(clientID),
    { enabled: !!clientID }
  );

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const clientName = client
    ? `${client.first_name} ${client.middle_name || ""} ${client.last_name}`.replace(/\s+/g, " ")
    : "Client";

  if (isLoading) return <Loader />;

  return (
    <Box sx={{ p: 2, minHeight: "100vh", bgcolor: "#f1f5f9" }}>
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2.5}>
          <Avatar
            sx={{
              width: 50,
              height: 50,
              bgcolor: theme.palette.primary.main,
              boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.25)}`,
              borderRadius: "16px",
            }}
          >
            <History sx={{ fontSize: 32 }} />
          </Avatar>
          <Box>
            <Typography
              variant="h5"
              fontWeight={900}
              sx={{ letterSpacing: "-0.5px", color: "#1e293b" }}
            >
              Visit Analysis
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <AutoGraph sx={{ fontSize: 12, color: theme.palette.primary.main }} />
              <Typography variant="body1" color="text.secondary" fontWeight={400}>
                Comprehensive insights for{" "}
                <b style={{ color: "#0f172a" }}>{clientName}</b>
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: "24px",
          boxShadow:
            "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
          overflow: "hidden",
          bgcolor: "background.paper",
          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        }}
      >
        <Box
          sx={{
            px: 3,
            pt: 1,
            bgcolor: alpha(theme.palette.primary.main, 0.01),
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              "& .MuiTabs-indicator": {
                height: 4,
                borderRadius: "4px 4px 0 0",
              },
              "& .MuiTab-root": {
                minHeight: 64,
                fontWeight: 700,
                fontSize: "0.95rem",
                textTransform: "none",
                mr: 2,
                color: "text.secondary",
                transition: "0.2s",
                "&.Mui-selected": {
                  color: "primary.main",
                },
                "&:hover": {
                  color: "primary.main",
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                },
              },
            }}
          >
            <Tab
              icon={<PersonPin sx={{ fontSize: 22 }} />}
              iconPosition="start"
              label="Direct Visits"
            />
            <Tab
              icon={<PhoneInTalk sx={{ fontSize: 22 }} />}
              iconPosition="start"
              label="Call of Visit"
            />
            <Tab
              icon={<Assignment sx={{ fontSize: 22 }} />}
              iconPosition="start"
              label="Client Observations"
            />
          </Tabs>
        </Box>

        <Box sx={{ p: { xs: 2, md: 4 } }}>
          {activeTab === 0 && (
            <Fade in={activeTab === 0} timeout={400}>
              <Box>
                <DirectVisit />
              </Box>
            </Fade>
          )}

          {activeTab === 1 && (
            <Fade in={activeTab === 1} timeout={400}>
              <Box>
                <VisitCall />
              </Box>
            </Fade>
          )}

          {activeTab === 2 && (
            <Fade in={activeTab === 2} timeout={400}>
              <Box>
                <CarePlanConfiguration fullName={clientName} />
              </Box>
            </Fade>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default VisitAnalysis;